/**
 * ListingVariantProvider — React context wrapping the listing page with a
 * pre-computed `ListingVariant` derived from `(location, configuration,
 * currentUser)`. Unit 2 of the listing-conversion overhaul plan.
 *
 * Consumers:
 *   - `useListingVariant()` → full variant
 *   - `useVariantSlice<K>(key)` → narrow slice (priceCard / leadForm / etc.)
 *
 * Admin-only querystring overrides (silently ignored for non-admins):
 *   - `?variant=monthly|broker|traditional|fleet` — forces the variant.
 *     The `fleet` value swaps to the fleet base variant directly
 *     (the resolver normally treats fleet as a sub-trigger of Monthly).
 *   - `?preview_as=anon` — feeds the resolver a `null` user so the
 *     variant computes as if the visitor were anonymous.
 *
 * The provider intentionally avoids `'use client'` — this is Pages Router
 * land. The file is `.tsx` because it returns JSX; the resolver and types
 * live in plain `.ts` modules so non-React surfaces (tests, future server
 * code) can import them without dragging React in.
 */
import { useRouter } from 'next/router'
import { createContext, ReactNode, useContext, useMemo } from 'react'

import { useAuthProvider } from 'src/context/AuthProvider'
import {
  IUserLike,
  ListingVariant,
  resolveListingVariant
} from 'src/lib/listingVariants'
import { baseFleetVariant } from 'src/lib/listingVariants/variants/fleet'
import { DealType, IConfiguration, ILocation } from 'src/types/graphql'
import { isBrokerVariant } from 'src/utils/pricing'

const ListingVariantContext = createContext<ListingVariant | null>(null)

interface ListingVariantProviderProps {
  location: ILocation
  configuration: IConfiguration
  children: ReactNode
}

/**
 * Querystring values accepted by the `?variant=` admin override. Anything
 * outside this set is treated as no-override (the resolver's default
 * `configuration.dealType` wins).
 */
type VariantOverride = 'monthly' | 'broker' | 'traditional' | 'fleet'

const VARIANT_OVERRIDE_TO_DEAL_TYPE: {
  [key in Exclude<VariantOverride, 'fleet'>]: DealType
} = {
  monthly: DealType.Monthly,
  broker: DealType.Broker,
  traditional: DealType.Traditional
}

function parseVariantOverride(raw: unknown): VariantOverride | null {
  if (typeof raw !== 'string') {
    return null
  }
  const normalized = raw.toLowerCase()
  if (
    normalized === 'monthly' ||
    normalized === 'broker' ||
    normalized === 'traditional' ||
    normalized === 'fleet'
  ) {
    return normalized
  }
  return null
}

/**
 * Build the fleet override variant: reuse the resolver for meta/admin
 * computation (and the rest of the contract), then layer `baseFleetVariant`
 * on top. Keeps a single, fully-typed `ListingVariant` exit point.
 */
function buildFleetOverride(
  location: ILocation,
  configuration: IConfiguration,
  user: IUserLike,
  derivedBrokerMode: boolean
): ListingVariant {
  const resolved = resolveListingVariant(
    location,
    configuration,
    user,
    derivedBrokerMode
  )
  return {
    ...resolved,
    priceCard: baseFleetVariant.priceCard,
    leadForm: baseFleetVariant.leadForm,
    sections: baseFleetVariant.sections,
    checkout: baseFleetVariant.checkout,
    meta: {
      ...resolved.meta,
      variantKey: 'fleet'
    }
  }
}

export function ListingVariantProvider({
  location,
  configuration,
  children
}: ListingVariantProviderProps) {
  const { currentUser } = useAuthProvider()
  const router = useRouter()

  const variantQuery = router.query.variant
  const previewAsQuery = router.query.preview_as

  // 99% of listings ship with `configuration.dealType` UNSET. The
  // canonical helper `isBrokerVariant` in `src/utils/pricing.ts`
  // applies the same `price > PRICE_THRESHOLD` heuristic the server's
  // `resolveDealType` uses. The boolean is passed to the pure resolver
  // as `derivedBrokerMode`; explicit `dealType` still wins (resolver
  // discrimination order: Traditional -> explicit Broker /
  // derivedBrokerMode / B2B-pillar -> Monthly).
  const derivedBrokerMode = isBrokerVariant(configuration)

  const variant = useMemo<ListingVariant>(() => {
    const isAdmin = Boolean(currentUser?.isAdmin)

    // `?preview_as=anon` — admin-only. Forces the resolver to see a null
    // user. Returning users keep their auth on every other path so we
    // don't accidentally show anon-only UX to logged-in admins.
    const previewAsAnon = isAdmin && previewAsQuery === 'anon'
    const effectiveUser: IUserLike = previewAsAnon
      ? null
      : currentUser
        ? { id: currentUser.id, isAdmin: Boolean(currentUser.isAdmin) }
        : null

    // `?variant=...` — admin-only. Anything else is silently ignored.
    const override = isAdmin ? parseVariantOverride(variantQuery) : null

    // Fleet override is a special case: the resolver doesn't auto-pick
    // fleet (fleet is a sub-trigger of Monthly + Parking). When an admin
    // forces fleet they want the fleet variant body directly.
    if (override === 'fleet') {
      return buildFleetOverride(
        location,
        configuration,
        effectiveUser,
        derivedBrokerMode
      )
    }

    // The three primary dealType overrides just swap the configuration's
    // dealType before handing it to the resolver. Everything else
    // (availability, locationType, etc.) is preserved so the override
    // composes naturally with the rest of the resolver's runtime logic.
    // When the admin forces an explicit dealType via `?variant=`, we pass
    // `derivedBrokerMode: false` so the explicit override always wins
    // over the price-derived fallback.
    let configForResolver: IConfiguration = configuration
    let derivedBrokerModeForResolver = derivedBrokerMode
    if (
      override === 'monthly' ||
      override === 'broker' ||
      override === 'traditional'
    ) {
      configForResolver = {
        ...configuration,
        dealType: VARIANT_OVERRIDE_TO_DEAL_TYPE[override]
      }
      derivedBrokerModeForResolver = false
    }

    return resolveListingVariant(
      location,
      configForResolver,
      effectiveUser,
      derivedBrokerModeForResolver
    )
  }, [
    location,
    configuration,
    currentUser,
    variantQuery,
    previewAsQuery,
    derivedBrokerMode
  ])

  return (
    <ListingVariantContext.Provider value={variant}>
      {children}
    </ListingVariantContext.Provider>
  )
}

export function useListingVariant(): ListingVariant {
  const variant = useContext(ListingVariantContext)
  if (!variant) {
    throw new Error(
      'useListingVariant() must be used inside <ListingVariantProvider>. ' +
        'Wrap the listing page in spotme-client/src/components/Details/index.tsx.'
    )
  }
  return variant
}

export function useVariantSlice<K extends keyof ListingVariant>(
  key: K
): ListingVariant[K] {
  return useListingVariant()[key]
}
